import React from "react";
import {
  Card,
  Group,
  Stack,
  Text,
  ThemeIcon,
  ActionIcon,
  NumberInput,
  Button,
} from "@mantine/core";
import {
  IconCurrencyDollar,
  IconCheck,
  IconTrash,
  IconPlus,
} from "@tabler/icons-react";

// Editable version of a scale table (revenue -> percentage), styled to match
// the read-only scale cards. Reusable wherever a scale needs to be edited.
//   lines        - array of { revenue_amount, percentage }
//   onChangeLine - (index, key, value) => void
//   onAddLine    - optional; shows an "Add Line" button when provided
//   onRemoveLine - optional; shows a per-row remove button when provided
//   onSave       - optional; shows a save (check) button in the header
//   onDelete     - optional; shows a delete (trash) button in the header
function EditableScaleCard({
  title = "SCALE",
  percentLabel = "%",
  lines,
  onChangeLine,
  onAddLine,
  onRemoveLine,
  onSave,
  onDelete,
}) {
  return (
    <Card withBorder radius="md" padding={0} style={{ overflow: "hidden" }}>
      {/* Header */}
      <Group justify="space-between" px="md" py="sm" bg="#faf4f0">
        <Group gap="sm">
          <ThemeIcon color="orange" variant="light" size={28} radius="md">
            <IconCurrencyDollar size={18} />
          </ThemeIcon>
          <Text
            fw={700}
            size="sm"
            c="dimmed"
            style={{ letterSpacing: "0.5px" }}
          >
            {title}
          </Text>
        </Group>
        {(onSave || onDelete) && (
          <Group gap="xs">
            {onSave && (
              <ActionIcon
                color="orange"
                variant="filled"
                onClick={onSave}
                aria-label="Save scale"
              >
                <IconCheck size={16} />
              </ActionIcon>
            )}
            {onDelete && (
              <ActionIcon
                color="gray"
                variant="light"
                onClick={onDelete}
                aria-label="Delete scale"
              >
                <IconTrash size={16} />
              </ActionIcon>
            )}
          </Group>
        )}
      </Group>

      {/* Column headers */}
      <Group
        justify="space-between"
        px="md"
        py="xs"
        bg="#F4F1EA"
        style={{ borderTop: "1px solid #ece0d8" }}
      >
        <Text size="xs" fw={700} c="#6b5a50">
          Retained Revenue
        </Text>
        <Text size="xs" fw={700} c="#6b5a50">
          {percentLabel}
        </Text>
      </Group>

      {/* Editable rows */}
      <Stack gap="xs" p="md">
        {lines.map((line, index) => (
          <Group key={index} gap="sm" wrap="nowrap">
            <NumberInput
              value={line.revenue_amount}
              onChange={value => onChangeLine(index, "revenue_amount", value)}
              min={0}
              prefix="$"
              thousandSeparator=","
              radius="sm"
              style={{ flex: 1 }}
            />
            <NumberInput
              value={line.percentage}
              onChange={value => onChangeLine(index, "percentage", value)}
              min={0}
              suffix="%"
              radius="sm"
              styles={{
                input: {
                  color: "#b9491c",
                  fontWeight: 700,
                  textAlign: "center",
                  borderColor: "#f0c4b4",
                },
              }}
              style={{ width: 110 }}
            />
            {onRemoveLine && (
              <ActionIcon
                color="red"
                variant="subtle"
                onClick={() => onRemoveLine(index)}
                disabled={lines.length === 1}
                aria-label="Remove line"
              >
                <IconTrash size={16} />
              </ActionIcon>
            )}
          </Group>
        ))}

        {onAddLine && (
          <Group justify="flex-start">
            <Button
              variant="subtle"
              size="xs"
              leftSection={<IconPlus size={14} />}
              onClick={onAddLine}
            >
              Add Line
            </Button>
          </Group>
        )}
      </Stack>
    </Card>
  );
}

export default EditableScaleCard;
